import React, { createContext, useContext, useState, useEffect } from 'react';
import { detectLocation } from './services/locationService';
import type { LocationState } from './types';
import { CONFIG } from './config';

interface LocationContextType extends LocationState {
  updateLocation: (location: Partial<LocationState>) => void;
  resetLocation: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LocationState>({
    ...CONFIG.DEFAULT_LOCATION,
    loading: true
  });

  const updateLocation = (newLocation: Partial<LocationState>) => {
    setState(prev => ({
      ...prev,
      ...newLocation,
      loading: false,
      error: undefined
    }));
  };

  const resetLocation = () => {
    setState({
      ...CONFIG.DEFAULT_LOCATION,
      loading: false
    });
  };

  useEffect(() => {
    let mounted = true;

    async function initLocation() {
      try {
        const location = await detectLocation();
        if (mounted) {
          setState(location);
        }
      } catch (error) {
        console.error('Location detection failed:', error);
        if (mounted) {
          setState({
            ...CONFIG.DEFAULT_LOCATION,
            loading: false,
            error: 'Could not detect location'
          });
        }
      }
    }

    initLocation();
    return () => { mounted = false; };
  }, []);

  return (
    <LocationContext.Provider value={{ ...state, updateLocation, resetLocation }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}